import { FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import ApiError from "@/model/ApiError";
import SignInLocationState from "@/model/SignInLocationState";
import "@/pages/SignInPage/SignInPage.scss";

function SignInPage() {
  const { t } = useTranslation();
  const { isAuthenticated, session, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const explicitRedirect = (location.state as SignInLocationState | null)?.from;

  if (isAuthenticated && session) {
    return <Navigate to={explicitRedirect ?? "/"} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      navigate(explicitRedirect ?? "/", {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t("auth.signIn.invalidCredentials"));
      } else {
        setError(t("auth.signIn.genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sign-in-page">
      <form className="sign-in-page__form" onSubmit={handleSubmit}>
        <h1>{t("auth.signIn.title")}</h1>

        <label className="sign-in-page__label" htmlFor="email">
          {t("auth.signIn.emailLabel")}
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        <label className="sign-in-page__label" htmlFor="password">
          {t("auth.signIn.passwordLabel")}
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {error && <p className="sign-in-page__error">{error}</p>}

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("auth.signIn.submitting") : t("auth.signIn.submit")}
        </button>

        <p className="sign-in-page__switch">
          {t("auth.signIn.noAccount")}{" "}
          <Link to="/signup" state={location.state}>{t("auth.signIn.signUpLink")}</Link>
        </p>
      </form>
    </div>
  );
}

export default SignInPage;
